import type { GalleryEspotItem, ImageItem, MediaGalleryHotspotContent, SketchfabItem, VideoItem } from '#types/media'
import { isObject } from '#core/utils/is'

type GalleryItem = ImageItem | VideoItem | SketchfabItem | GalleryEspotItem

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const hasMatchingImagePublicId = (src: string, imagePublicId: string) =>
  new RegExp(`/${escapeRegExp(imagePublicId)}(?:/|\\.|\\?|#|$)`).test(src)

const isGalleryHotspot = (value: unknown): value is MediaGalleryHotspotContent =>
  isObject(value)
  && value.type === 'VfCanvasMediaGalleryHotspot'
  && typeof value.id === 'string'
  && typeof value.imagePublicId === 'string'
  && typeof value.imageUrl === 'string'
  && typeof value.width === 'number'
  && Number.isFinite(value.width)
  && value.width > 0
  && typeof value.height === 'number'
  && Number.isFinite(value.height)
  && value.height > 0
  && Array.isArray(value.imageMap)

/**
 * Attaches valid CMS hotspots to matching PDP gallery images.
 * @param mediaItems - Product gallery items after their display transformations and eSpot insertion.
 * @param sectionItems - Content from the `pdp-media-gallery` eSpot section.
 * @returns Gallery items with up to four hotspots on each matched image.
 * @category Utils
 */
export const attachGalleryHotspots = (mediaItems: GalleryItem[], sectionItems?: unknown[]): GalleryItem[] => {
  if (!sectionItems?.length) return mediaItems

  let result = mediaItems

  for (const sectionItem of sectionItems) {
    if (!isGalleryHotspot(sectionItem)) continue

    const matchedIndex = result.findIndex((item) =>
      item.type === 'image' && hasMatchingImagePublicId(item.src, sectionItem.imagePublicId))
    if (matchedIndex < 0) continue

    const image = result[matchedIndex]
    if (image.type !== 'image') continue

    if (result === mediaItems) result = [...mediaItems]
    result[matchedIndex] = {
      ...image,
      hotspotMedia: { ...sectionItem, imageMap: sectionItem.imageMap?.slice(0, 4) }
    }
  }

  return result
}
